import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import {
  Skeleton,
  SkeletonAvatarRow,
  SkeletonCardBlock,
  SkeletonStatus,
  SkeletonTableRows,
  SkeletonTextBlock,
} from './skeleton';

function InfoBannerSkeleton() {
  return <Skeleton className="h-20 w-full rounded-2xl sm:h-[5.5rem] sm:rounded-3xl" />;
}

function StatCardsRow({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-16 w-full rounded-lg sm:h-20 sm:rounded-xl" />
      ))}
    </div>
  );
}

export function AuthBootstrapSkeleton() {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <SkeletonStatus label={t('common.loading')} className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-6">
          <Skeleton className="mx-auto mb-6 h-8 w-48 max-w-full" />
          <SkeletonCardBlock />
        </div>
      </SkeletonStatus>
    </div>
  );
}

export function CabinetTablePageSkeleton({
  statCount = 4,
  tableRows = 6,
  className,
  showInfoBanner = true,
  showStats = true,
  statusLabel,
}: {
  statCount?: number;
  tableRows?: number;
  className?: string;
  showInfoBanner?: boolean;
  showStats?: boolean;
  statusLabel?: string;
}) {
  const { t } = useTranslation('common');
  const label = statusLabel ?? t('common.loading');

  return (
    <SkeletonStatus label={label} className={cn('page-stack', className)}>
      {showInfoBanner ? <InfoBannerSkeleton /> : null}
      {showStats && statCount > 0 ? <StatCardsRow count={statCount} /> : null}
      <div className="surface-card p-4 sm:p-6">
        <Skeleton className="mb-4 h-10 w-full max-w-xs rounded-lg" />
        <SkeletonTableRows rows={tableRows} />
      </div>
    </SkeletonStatus>
  );
}

export function CabinetListPageSkeleton({ items = 4 }: { items?: number }) {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="page-stack">
      <InfoBannerSkeleton />
      <div className="list-stack">
        {Array.from({ length: items }).map((_, index) => (
          <div key={index} className="surface-card p-4 sm:p-5">
            <SkeletonAvatarRow />
          </div>
        ))}
      </div>
    </SkeletonStatus>
  );
}

export function BalancePageSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="page-stack">
      <Skeleton className="h-32 w-full rounded-2xl sm:h-36 sm:rounded-3xl" />
      <StatCardsRow count={4} />
      <Skeleton className="h-64 w-full rounded-2xl sm:h-80 sm:rounded-3xl" />
    </SkeletonStatus>
  );
}

export function WalletsPageSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="page-stack">
      <InfoBannerSkeleton />
      <StatCardsRow count={6} />
      <div className="surface-card p-4">
        <Skeleton className="mb-4 h-10 w-full max-w-sm" />
        <SkeletonTableRows rows={4} rowClassName="h-14 rounded-lg" />
      </div>
    </SkeletonStatus>
  );
}

export function ReferralsPageSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="page-stack">
      <div className="surface-card p-4 sm:p-6">
        <SkeletonAvatarRow className="mb-6" />
        <SkeletonCardBlock />
      </div>
      <div className="surface-card p-4 sm:p-6">
        <Skeleton className="mb-4 h-10 w-48" />
        <SkeletonTableRows rows={6} />
      </div>
    </SkeletonStatus>
  );
}

export function SecurityPageSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="space-y-6">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="space-y-4 rounded-xl border border-border bg-card p-4">
          <Skeleton className="h-6 w-40" />
          <SkeletonTextBlock />
          <Skeleton className="h-10 w-36 rounded-lg" />
        </div>
      ))}
    </SkeletonStatus>
  );
}

export function InvoiceDetailSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="space-y-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="grid grid-cols-1 items-center gap-y-2 sm:grid-cols-[minmax(120px,175px)_1fr] sm:gap-y-2"
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-full max-w-md" />
        </div>
      ))}
      <Skeleton className="h-32 w-full rounded-lg" />
    </SkeletonStatus>
  );
}

export function DialogFormFieldsSkeleton({ fields = 4 }: { fields?: number }) {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="space-y-4">
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <Skeleton className="h-10 w-28 rounded-lg" />
      </div>
    </SkeletonStatus>
  );
}

export function VerifyEmailCardSkeleton() {
  const { t } = useTranslation('common');

  return (
    <SkeletonStatus label={t('common.loading')} className="space-y-4 py-2">
      <Skeleton className="mx-auto h-6 w-48" />
      <SkeletonTextBlock className="mx-auto items-center" primaryClassName="mx-auto" secondaryClassName="mx-auto" />
      <Skeleton className="mx-auto h-10 w-full rounded-lg" />
    </SkeletonStatus>
  );
}

export function CatalogListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-1 px-1 py-2">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-9 w-full rounded-md" />
      ))}
    </div>
  );
}
